import type { ComputedRef, Ref, WritableComputedRef } from 'vue';
import { computed, ref, watch } from 'vue';

import { Currency, Money } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import { LineItem } from '@/model';
import { MoneyUtil } from '@/util';

export interface UseValueOfGoodsResponse {
  effectiveValueOfGoods: WritableComputedRef<Money | undefined>;
  lineItemsValue: ComputedRef<Money>;
  wasClearedByDirectEdit: Ref<boolean>;
}

/**
 * Keeps a value of goods in sync with the sum of its line item values. The sum
 * drives the value in both directions, up and down. A direct edit takes
 * precedence and zeroes the line item values out so the sum stops driving it.
 *
 * @param valueOfGoods the value of goods on the order or shipment
 * @param lineItems the line items whose values are summed
 */
export const useValueOfGoods = (
  valueOfGoods: Ref<Money | undefined>,
  lineItems: Ref<LineItem[]>,
): UseValueOfGoodsResponse => {
  const wasClearedByDirectEdit = ref(false);

  const lineItemsValue = computed(() =>
    lineItems.value
      .map((li) => li.valueOfGoods)
      .filter(notNull)
      .reduce((a, b) => b.plus(a), Money.zero(Currency.USD)),
  );

  const hasLineItemsValue = computed(
    () => !MoneyUtil.equals(lineItemsValue.value, Money.zero(Currency.USD)),
  );

  //a no op write would mark the model dirty
  const writeValueOfGoods = (newVal: Money | undefined) => {
    if (!MoneyUtil.equals(valueOfGoods.value, newVal)) {
      valueOfGoods.value = newVal;
    }
  };

  const effectiveValueOfGoods = computed({
    //the summed line item values, or the directly entered value once they are zero
    get: () =>
      hasLineItemsValue.value ? lineItemsValue.value : valueOfGoods.value,
    set: (newVal: Money | undefined) => {
      //a direct edit takes precedence, so zero the line items out
      if (
        hasLineItemsValue.value &&
        !MoneyUtil.equals(newVal, lineItemsValue.value)
      ) {
        lineItems.value.forEach(
          (li) => (li.valueOfGoods = Money.zero(Currency.USD)),
        );
        wasClearedByDirectEdit.value = true;
      }
      writeValueOfGoods(newVal);
    },
  });

  watch(lineItemsValue, (newVal, oldVal) => {
    if (MoneyUtil.equals(newVal, Money.zero(Currency.USD))) {
      //still the old sum means no direct edit overrode it, so follow to zero
      if (MoneyUtil.equals(valueOfGoods.value, oldVal)) {
        writeValueOfGoods(newVal);
      }
      return;
    }

    //the line items are driving the value again
    wasClearedByDirectEdit.value = false;
    writeValueOfGoods(newVal);
  });

  return { effectiveValueOfGoods, lineItemsValue, wasClearedByDirectEdit };
};
